<template>
  <div class="stat-card-professional">
    <div class="stat-content">
      <div class="stat-value">
        <span class="stat-number-large">{{ displayNumber }}</span>
      </div>
      <div class="stat-description">
        <span class="stat-label-text">{{ label }}</span>
      </div>
    </div>
    <div class="stat-indicator" :class="`indicator-${index + 1}`"></div>
  </div>
</template>

<script>
export default {
  name: 'StatCard',
  props: {
    icon: {
      type: String,
      required: true
    },
    number: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      default: 0
    },
    animate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      displayNumber: '0',
      animationFrame: null
    }
  },
  watch: {
    animate(newVal) {
      if (newVal) {
        this.animateNumber()
      }
    }
  },
  beforeUnmount() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }
  },
  methods: {
    animateNumber() {
      const match = this.number.match(/(\d+\.?\d*)/)
      if (!match) {
        this.displayNumber = this.number
        return
      }

      const targetValue = parseFloat(match[1])
      const suffix = this.number.replace(match[1], '')
      const duration = 2000 // Durata in millisecondi
      const startTime = performance.now()

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        // Easing function per un'animazione più naturale
        const easeOutQuart = 1 - Math.pow(1 - progress, 4)
        const current = targetValue * easeOutQuart
        
        // Formatta il numero
        let formattedNumber
        if (progress >= 0.98) {
          // Negli ultimi frame, usa il valore esatto originale
          formattedNumber = targetValue
        } else {
          formattedNumber = current % 1 === 0 
            ? Math.floor(current) 
            : current.toFixed(1)
        }
        
        this.displayNumber = formattedNumber + suffix

        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(animate)
        } else {
          // Usa il valore originale esatto, non arrotondato
          this.displayNumber = this.number
          this.animationFrame = null
        }
      }

      this.animationFrame = requestAnimationFrame(animate)
    }
  }
}
</script>

<style lang="scss" scoped>
.stat-card-professional {
  position: relative;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  padding: 28px 24px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 255, 255, 0.3), 
      transparent
    );
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
    transform: translateY(-4px);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.2),
      0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    
    &::before {
      opacity: 1;
    }

    .stat-indicator {
      transform: scale(1.1);
      opacity: 0.9;
    }

    .stat-number-large {
      transform: scale(1.05);
    }
  }

  @media (max-width: 768px) {
    padding: 20px 18px;
  }
}

.stat-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.stat-number-large {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1;
  color: #ffffff;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: inline-block;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.stat-description {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label-text {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
}

.stat-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.6;
  transition: all 0.4s ease;
  z-index: 1;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
}

.indicator-1 {
  background: radial-gradient(circle, #667eea 0%, #764ba2 100%);
}

.indicator-2 {
  background: radial-gradient(circle, #f093fb 0%, #f5576c 100%);
}

.indicator-3 {
  background: radial-gradient(circle, #4facfe 0%, #00f2fe 100%);
}
</style>
